import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import {
  alignItems,
  borderRadius,
  display,
  height,
  justifyContent,
  maxHeight,
  maxWidth,
  overflow,
  width,
} from 'styled-system';

import { useOpenDrawer } from '../../hooks/drawer';
import { cursor } from '../../theme/system-utilities';
import { Button } from '../Button';
import Icon from '../Icon';
import { BodyRegularBR1 } from '../Typography';

interface CtaButtonWithDrawerProps extends Record<
  'ctaLabel' | 'ctaColor' | 'ctaDisclaimerText',
  Nullable<string>
> {
  ctaAriaLabel?: string;
  className?: string;
  displayFlexRow?: boolean;
}

const ICON_SIZES = ['20px', null, null, '24px'];
const INLINE_LAYOUT_TEXT_COLOR = 'sys.neutral.text.strong';

const CtaButton = styled(Button)<any>`
  ${display};
  ${justifyContent};
  ${alignItems};
  ${width};
  ${maxWidth};
  ${cursor};
  ${borderRadius};
`;

const ctaButtonStyles: ComponentProps<typeof CtaButton> = {
  display: 'flex',
  width: ['100%', 'auto'],
  maxWidth: '100%',
  justifyContent: ['center', 'flex-end'],
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: 'medium',
};

const CtaText = styled(BodyRegularBR1)<any>`
  ${overflow};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ctaTextStyles: ComponentProps<typeof CtaText> = {
  as: 'p',
  m: 0,
  overflow: 'hidden',
};

const ArrowRight = styled(Icon)<any>`
  ${width};
  ${height};
  ${maxWidth};
  ${maxHeight};

  fill: ${({ color: iconColor }) => iconColor};
`;

const arrowRightStyles: ComponentProps<typeof ArrowRight> = {
  width: ICON_SIZES,
  height: ICON_SIZES,
  maxWidth: ICON_SIZES,
  maxHeight: ICON_SIZES,
  name: 'arrowRight',
};

const CtaButtonWithDrawer = ({
  ctaLabel,
  ctaColor,
  ctaAriaLabel,
  ctaDisclaimerText,
  className,
  displayFlexRow,
}: CtaButtonWithDrawerProps) => {
  const showDisclaimerDrawer = useOpenDrawer({
    html: ctaDisclaimerText,
  });
  const color = displayFlexRow ? INLINE_LAYOUT_TEXT_COLOR : ctaColor;

  return (
    <CtaButton
      {...ctaButtonStyles}
      kind="bare"
      onClick={showDisclaimerDrawer}
      className={className}
      {...(ctaAriaLabel && { ['aria-label']: ctaAriaLabel })}
    >
      <CtaText {...ctaTextStyles} color={color}>
        {ctaLabel}
      </CtaText>
      <ArrowRight {...arrowRightStyles} color={color} />
    </CtaButton>
  );
};

export default CtaButtonWithDrawer;
